import { useState } from 'react'

/**
 * One retry at full size before a card gives up on a picture.
 *
 * The server asks each CDN for a card-sized variant rather than the original upload — see
 * `artwork.py` for why, and for how much it saves. Ticketmaster is the one host that does
 * not always have one: it names variants in the path, and for a minority of its assets the
 * named variant answers 403 while the original answers 200.
 *
 * Every card already falls back to a "no picture" plate, so the failure was never ugly — it
 * was just a picture we had, thrown away to save bytes we then did not save. So a card steps
 * down instead: variant, then original, then the plate.
 *
 * **Where that step-down happens has moved.** The calendar's artwork is no longer a CDN URL
 * in the browser at all — `artwork.served` hands out an address on this site, and the route
 * behind it holds the URL *as stored*, so it asks for the variant and falls back to the
 * upload itself, with nothing guessed. What is left here is the guess a browser had to make
 * because the rewritten URL was all it held: two stored shapes collapse onto one card URL,
 * so there is no way back to which one it came from, and `_SOURCE` is the guess. It still
 * fires for any picture handed to the browser as somebody else's URL; for a proxied one
 * `fullSize` finds no variant suffix, returns null, and the card goes straight to the plate —
 * which is right, because the server already tried both.
 */
const TICKETMASTER_SIZES = ['_RETINA_PORTRAIT_3_2.jpg', '_RETINA_LANDSCAPE_16_9.jpg']

export function fullSize(url: string): string | null {
  // Both sizes, because the sheet asks for the 16:9 and a card asks for the 3:2, and
  // Ticketmaster's missing-variant problem is per asset rather than per size — when it has
  // none of them it has none of them.
  for (const size of TICKETMASTER_SIZES) {
    if (url.endsWith(size)) return `${url.slice(0, -size.length)}_SOURCE`
  }
  return null
}

/**
 * The `src` a card should draw, and what to do when it fails.
 *
 * `broken` means both steps are spent — draw the plate. Keyed on `url` so a recycled card
 * (the month grid reuses its chips as you page) does not inherit the last gig's verdict.
 *
 * **The reset happens during render, not in an effect.** The state records which `url` it
 * belongs to, and a render that is handed a different one replaces it before returning. React
 * discards that render and runs the component again with the replaced state before committing
 * anything, so the first commit that draws a URL already carries that URL's state.
 *
 * A reset in `useEffect` runs after the `<img>` is in the document. An `error` the browser
 * reports in between is stepped down first and then wiped by the queued reset, which leaves
 * the card on a URL that has already failed. On a URL change the same gap also commits the
 * previous URL's `src` or `broken` once, so a recycled chip draws the last gig's picture or
 * plate, and an error in that commit is judged against the wrong URL. `useLayoutEffect` closes
 * the first gap and not the second, because it too runs after the commit it would correct.
 *
 * The comparison is against the `url` the state was made for, never against `src`: once a
 * card has stepped to the original, `src` differs from `url` by design, and starting over on
 * that difference would undo the step on the next render.
 *
 * An error is about the picture one render drew, so `onError` acts only while the state is
 * still the one that render saw — same `url`, same `src`. An error from before a URL change,
 * or a second one against a picture already stepped past, is ignored rather than stepping the
 * current picture down from one that is no longer drawn.
 */
export function useArtwork(url: string | null): {
  src: string | null
  broken: boolean
  onError: () => void
} {
  const [state, setState] = useState({ url, src: url, broken: false })

  let current = state
  if (state.url !== url) {
    current = { url, src: url, broken: false }
    setState(current)
  }

  const { src, broken } = current
  return {
    src,
    broken,
    onError: () => {
      setState((now) => {
        if (now.url !== url || now.src !== src) return now
        // `fullSize` is the one place a URL's second step is decided; nothing here reads the
        // suffix itself.
        const full = src ? fullSize(src) : null
        return full ? { url, src: full, broken: false } : { url, src, broken: true }
      })
    },
  }
}
